import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import {
  generateVerificationInformationSupportingInformationSchema,
  verificationInformationSupportingInformationAnswersToSupportingInformation,
} from '@/features/verification/schemas/supporting-information';
import { getVerificationInformationSupportingInformation, postVerificationInformationSupportingInformation } from '@/services/api/profile/verification-information';

const profileVerificationInformationSupportingInformationQueryKey = (pid: UserPid) => ['profile', 'verification-information-supporting-information', pid];

const profileVerificationInformationSupportingInformationQueryFn = async () => {
  const supportingInformation = await getVerificationInformationSupportingInformation();
  const { requiredFields, answers } = supportingInformation;
  const schema = generateVerificationInformationSupportingInformationSchema(requiredFields);
  const isRequired = requiredFields.length > 0;
  const isComplete = isRequired ? schema.safeParse(verificationInformationSupportingInformationAnswersToSupportingInformation(requiredFields, answers)).success : true;

  return {
    supportingInformation,
    isComplete,
    isRequired,
  };
};
const profileVerificationInformationSupportingInformationQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: profileVerificationInformationSupportingInformationQueryFn,
    queryKey: profileVerificationInformationSupportingInformationQueryKey(pid),
  });

const useProfileVerificationInformationSupportingInformation = (enabled = true) => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const { enabled: queryOptionsEnabled, ...queryOptions } = profileVerificationInformationSupportingInformationQueryOptions(pid);
  const { data, ...rest } = useQuery({
    ...queryOptions,
    enabled: queryOptionsEnabled && enabled,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: profileVerificationInformationSupportingInformationQueryKey(pid) });
  }, [pid, queryClient]);

  return {
    ...rest,
    data: data?.supportingInformation,
    isComplete: data?.isComplete,
    isRequired: data?.isRequired,
    invalidate,
  };
};

const useProfileVerificationInformationSupportingInformationMutation = () => {
  return useMutation({
    mutationFn: postVerificationInformationSupportingInformation,
  });
};

export { useProfileVerificationInformationSupportingInformation, useProfileVerificationInformationSupportingInformationMutation };
